import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SkillDetailPanel = ({ skill, isOpen, onClose, relatedProjects = [] }) => {
  if (!skill) return null;

  const getLevelText = (level) => {
    const levels = ['Beginner', 'Intermediate', 'Proficient', 'Advanced', 'Expert'];
    return levels?.[level - 1] || 'Unknown';
  };

  const getExperienceColor = (years) => {
    if (years >= 5) return 'text-green-400';
    if (years >= 3) return 'text-yellow-400';
    if (years >= 1) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-4 top-1/2 transform -translate-y-1/2 w-96 max-w-[90vw] glass-panel rounded-xl shadow-cosmic-lg z-50 max-h-[80vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="p-6 border-b border-border/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 bg-gradient-to-r ${
                    skill?.category === 'technical' ? 'from-accent to-blue-400' :
                    skill?.category === 'creative'? 'from-cosmic-energy to-pink-400' : 'from-cosmic-cta to-purple-400'
                  } rounded-lg flex items-center justify-center`}>
                    <Icon name={skill?.icon} size={24} className="text-background" />
                  </div>
                  <div>
                    <h3 className="font-headline text-xl font-bold text-foreground">
                      {skill?.name}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground">
                      {skill?.category} skill
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              {/* Proficiency Level */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-body text-sm text-muted-foreground">
                    Proficiency Level
                  </span>
                  <span className="font-body text-sm font-medium text-accent">
                    {getLevelText(skill?.level)}
                  </span>
                </div>
                <div className="w-full bg-surface rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-accent to-cosmic-energy h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(skill?.level / 5) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
                  About This Skill
                </h4>
                <p className="font-body text-muted-foreground leading-relaxed">
                  {skill?.description}
                </p>
              </div>

              {/* Experience Timeline */}
              <div>
                <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
                  Experience Timeline
                </h4>
                <div className="space-y-3">
                  {skill?.timeline?.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0" />
                      <div>
                        <p className="font-body text-sm font-medium text-foreground">
                          {item?.milestone}
                        </p>
                        <p className="font-body text-xs text-muted-foreground">
                          {item?.year} • {item?.context}
                        </p>
                      </div>
                    </div>
                  )) || (
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={16} className="text-accent" />
                      <span className={`font-body text-sm ${getExperienceColor(skill?.yearsOfExperience)}`}>
                        {skill?.yearsOfExperience} years of experience
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Related Projects */}
              {relatedProjects?.length > 0 && (
                <div>
                  <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
                    Featured Projects
                  </h4>
                  <div className="space-y-2">
                    {relatedProjects?.slice(0, 3)?.map((project, index) => (
                      <div 
                        key={index}
                        className="p-3 bg-surface/30 rounded-lg border border-border/10 hover:border-accent/30 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-accent to-cosmic-energy rounded-md flex items-center justify-center">
                            <Icon name="Folder" size={16} className="text-background" />
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium text-foreground">
                              {project?.name}
                            </p>
                            <p className="font-body text-xs text-muted-foreground">
                              {project?.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Skill Connections */}
              {skill?.connections && skill?.connections?.length > 0 && (
                <div>
                  <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
                    Skill Synergies
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {skill?.connections?.map((connection, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-accent/10 text-accent rounded-full font-body text-xs border border-accent/20"
                      >
                        {connection}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Learning Resources */}
              {skill?.resources && skill?.resources?.length > 0 && (
                <div>
                  <h4 className="font-headline text-lg font-semibold text-foreground mb-3">
                    Learning Resources
                  </h4>
                  <div className="space-y-2">
                    {skill?.resources?.map((resource, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Icon name="ExternalLink" size={14} className="text-accent" />
                        <span className="font-body text-sm text-muted-foreground">
                          {resource}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {skill?.isRecent && (
                    <span className="px-2 py-1 bg-cosmic-cta/20 text-cosmic-cta rounded-full font-body text-xs border border-cosmic-cta/30">
                      Recently Added
                    </span>
                  )}
                  {skill?.isCertified && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full font-body text-xs border border-green-500/30">
                      Certified
                    </span>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  View Projects
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SkillDetailPanel;